// /////////////////////////////////////////////////////////////
// [ファイル] SfaProjectCreatePage.tsx
// [概要] 営業案件の新規登録（推進案件機能統合 リリース1）
//        区分の選択は無い。案件名など共通項目で登録でき、仕入契約の入力は任意。
//        販売情報を入力しなくても区画が 1 つ自動作成される（仕入ありは販売前＝0.未着手で集計対象外、売上のみは 1.見込み）。
// /////////////////////////////////////////////////////////////
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Checkbox, FormControlLabel, IconButton, MenuItem, Paper, TextField, Typography } from '@mui/material';
import * as Icons from '../../constants/icons';
import { STANDARD_CTA_BUTTON_SX, STANDARD_TEXT_BUTTON_SX } from '../../constants/buttonStyles';
import { CATEGORY_LARGE, CATEGORY_MEDIUM_BY_LARGE } from '../../constants/masterData';
import LoadingView from '../../components/LoadingView';
import { useAuth } from '../../firebase/AuthProvider';
import { useNotification } from '../../providers/NotificationProvider';
import { useMasterData } from '../../providers/MasterDataProvider';
import CustomerPicker, { CustomerOption } from '../../components/sfa/CustomerPicker';
import { NEW_CONTRACT_PHASES } from '../../components/sfa/ContractPanel';
import { AnkenNumberPicker } from '../../components/sfa/AnkenNumberPicker';
import { sfaStructureApi, fillStaffFields, purchasePhaseLabel, UNIT_KINDS } from '../../hooks/useSfaStructure';

// 入力欄を包む枠。コンポーネント内で定義すると描画のたびに作り直され入力欄がフォーカスを失うため、外に置く
const Section = ({ title, sub, children }: { title: string; sub?: string; children: React.ReactNode }) => (
  <Paper sx={{ p: 2, mb: 1.5 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
    {sub && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{sub}</Typography>}
    {children}
  </Paper>
);

export default function SfaProjectCreatePage() {
  const navigate = useNavigate();
  const notify = useNotification();
  const { user } = useAuth();
  const { staffList } = useMasterData();
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState<any>({ project_name: '', land_name: '', category_large: '', category_medium: '', main_staff_user_id: '', owner_id: '', support_staff_id: '' });
  const [withBuy, setWithBuy] = useState(false);
  const [withSale, setWithSale] = useState(true);
  const [c, setC] = useState<any>({ seller: null as CustomerOption | null, expected_contract_ym: '', contract_date: '', settlement_date: '', estimated_amount: '', contract_amount: '', brokerage_fee: '', system_numbers: [] as string[], phase: NEW_CONTRACT_PHASES[0], parcels: [{ location: '', chiban: '', land_area: '' }] });
  const [u, setU] = useState<any>({ customers: [] as CustomerOption[], expected_order_ym: '', order_confirmed_date: '', sales_amount: '', construction_cost: '', unit_kind: '注文住宅', system_numbers: [] as string[] });
  const [picker, setPicker] = useState<CustomerOption | null>(null);

  useEffect(() => {
    if (!f.main_staff_user_id && user?.email && staffList.some((s: any) => s.staff_id === user.email)) setF((p: any) => ({ ...p, main_staff_user_id: user.email }));
  }, [user, staffList, f.main_staff_user_id]);

  const mediums = CATEGORY_MEDIUM_BY_LARGE[f.category_large] || [];

  const save = async () => {
    if (!f.project_name.trim()) { notify.warning('案件名は必須です'); return; }
    if (withBuy && !c.seller) { notify.warning('仕入契約を登録する場合、地主（売主）は必須です'); return; }
    setSaving(true);
    try {
      // 所有者が未選択ならメイン担当を所有者にする (旧画面と同じ既定)
      const body: any = fillStaffFields({ ...f, owner_id: f.owner_id || f.main_staff_user_id, operator_id: user?.email }, staffList);
      if (withBuy) {
        body.contract = {
          seller_customer_id: c.seller?.id, expected_contract_ym: c.expected_contract_ym, contract_date: c.contract_date, settlement_date: c.settlement_date,
          estimated_amount: c.estimated_amount, contract_amount: c.contract_amount, brokerage_fee: c.brokerage_fee, system_numbers: c.system_numbers, phase: c.phase,
          parcels: c.parcels.filter((p: any) => p.chiban.trim()).map((p: any) => ({ location: p.location || null, chiban: p.chiban.trim(), land_area: p.land_area || null })),
        };
      }
      if (withSale) {
        body.unit = {
          unit_kind: u.unit_kind, expected_order_ym: u.expected_order_ym, order_confirmed_date: u.order_confirmed_date, sales_amount: u.sales_amount, construction_cost: u.construction_cost,
          system_numbers: u.system_numbers,
          customers: u.customers.map((x: CustomerOption, i: number) => ({ customer_id: x.id, is_representative: i === 0 })),
        };
      }
      const r = await sfaStructureApi.createProject(body);
      notify.success(`案件「${f.project_name}」を登録しました（タイプ: ${withBuy ? '仕入あり' : '売上のみ'}${withSale ? '' : withBuy ? '／販売区画は「販売前」で自動作成' : '／区画を「1.見込み」で自動作成'}）`);
      navigate(`/sfa/projects/${r.project_id}`);
    } catch (e: any) {
      notify.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (saving) return <LoadingView message="登録中..." height="80vh" />;

  return (
    <Box sx={{ py: 2, width: '92%', maxWidth: 1100, margin: '0 auto', boxSizing: 'border-box' }}>
      <Button startIcon={<Icons.ArrowBack />} sx={STANDARD_TEXT_BUTTON_SX} onClick={() => navigate('/sfa/projects')}>一覧に戻る</Button>
      <Typography variant="h6" sx={{ fontWeight: 700, my: 1 }}>新規案件の登録</Typography>

      <Section title="案件の基本情報" sub="区分の選択はありません。ここまでで登録できます。土地の仕入契約は後からいつでも追加でき、販売区画は入力しなくても 1 つ自動で作られます。">
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}>
          <TextField size="small" label="案件名" required value={f.project_name} onChange={(e) => setF({ ...f, project_name: e.target.value })} sx={{ gridColumn: 'span 2' }} placeholder="例: 広島市西区高須台2_分譲開発" />
          <TextField select size="small" label="メイン担当" value={f.main_staff_user_id} onChange={(e) => setF({ ...f, main_staff_user_id: e.target.value })}>
            {staffList.map((s: any) => <MenuItem key={s.staff_id} value={s.staff_id}>{s.staff_name}（{s.branch_name}）</MenuItem>)}
          </TextField>
          <TextField select size="small" label="所有者" value={f.owner_id} onChange={(e) => setF({ ...f, owner_id: e.target.value })} helperText="空欄ならメイン担当と同じ">
            <MenuItem value="">（メイン担当と同じ）</MenuItem>
            {staffList.map((s: any) => <MenuItem key={s.staff_id} value={s.staff_id}>{s.staff_name}（{s.branch_name}）</MenuItem>)}
          </TextField>
          <TextField select size="small" label="サポート担当者" value={f.support_staff_id} onChange={(e) => setF({ ...f, support_staff_id: e.target.value })}>
            <MenuItem value="">（なし）</MenuItem>
            {staffList.map((s: any) => <MenuItem key={s.staff_id} value={s.staff_id}>{s.staff_name}（{s.branch_name}）</MenuItem>)}
          </TextField>
          <Box />
          <TextField size="small" label="物件所在地" value={f.land_name} onChange={(e) => setF({ ...f, land_name: e.target.value })} sx={{ gridColumn: 'span 3' }} />
          <TextField select size="small" label="大項目" value={f.category_large} onChange={(e) => setF({ ...f, category_large: e.target.value, category_medium: '' })}>
            {CATEGORY_LARGE.map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="中項目" value={f.category_medium} onChange={(e) => setF({ ...f, category_medium: e.target.value })} disabled={!mediums.length}>
            {mediums.map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
          </TextField>
        </Box>
      </Section>

      <Section title="土地の仕入契約（任意）">
        <FormControlLabel control={<Checkbox checked={withBuy} onChange={(e) => setWithBuy(e.target.checked)} />} label="土地の仕入契約をいま登録する（登録するとタイプが「仕入あり」になる。後から案件詳細の「仕入契約を追加」で追加しても同じ）" />
        {withBuy && (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mt: 1 }}>
            <Box sx={{ gridColumn: 'span 2' }}><CustomerPicker label="地主（売主・1 人）" required value={c.seller} onChange={(v) => setC({ ...c, seller: v })} /></Box>
            <TextField size="small" type="month" label="契約予定年月" value={c.expected_contract_ym} onChange={(e) => setC({ ...c, expected_contract_ym: e.target.value })} slotProps={{ inputLabel: { shrink: true } }} />
            <TextField size="small" type="date" label="契約日" value={c.contract_date} onChange={(e) => setC({ ...c, contract_date: e.target.value })} slotProps={{ inputLabel: { shrink: true } }} />
            <TextField size="small" type="date" label="決済日" value={c.settlement_date} onChange={(e) => setC({ ...c, settlement_date: e.target.value })} slotProps={{ inputLabel: { shrink: true } }} helperText="入力時は契約日も必須" />
            <TextField select size="small" label="仕入フェーズ" value={c.phase} onChange={(e) => setC({ ...c, phase: e.target.value })} helperText="契約日を入れると 5、決済日を入れると 6 になります（決済予定の日付でも可）">
              {NEW_CONTRACT_PHASES.map((p) => <MenuItem key={p} value={p}>{purchasePhaseLabel(p)}</MenuItem>)}
            </TextField>
            <TextField size="small" label="想定金額（円）" value={c.estimated_amount} onChange={(e) => setC({ ...c, estimated_amount: e.target.value })} />
            <TextField size="small" label="契約金額（円）" value={c.contract_amount} onChange={(e) => setC({ ...c, contract_amount: e.target.value })} placeholder="契約後に入力" />
            <TextField size="small" label="仕入時仲介手数料（円）" value={c.brokerage_fee} onChange={(e) => setC({ ...c, brokerage_fee: e.target.value })} />
            <AnkenNumberPicker value={c.system_numbers} onChange={(v) => setC({ ...c, system_numbers: v })} sx={{ gridColumn: 'span 3' }} />
            <Box sx={{ gridColumn: 'span 3' }}>
              <Typography variant="caption" color="text.secondary">筆（任意。登記 DB からの候補検索は登録後の「土地仕入」タブから）</Typography>
              {c.parcels.map((p: any, i: number) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, mt: 0.75 }}>
                  <TextField size="small" label="所在" value={p.location} onChange={(e) => setC({ ...c, parcels: c.parcels.map((x: any, j: number) => j === i ? { ...x, location: e.target.value } : x) })} sx={{ flex: 2 }} />
                  <TextField size="small" label="地番" value={p.chiban} onChange={(e) => setC({ ...c, parcels: c.parcels.map((x: any, j: number) => j === i ? { ...x, chiban: e.target.value } : x) })} sx={{ flex: 1 }} />
                  <TextField size="small" label="地積（㎡）" type="number" value={p.land_area} onChange={(e) => setC({ ...c, parcels: c.parcels.map((x: any, j: number) => j === i ? { ...x, land_area: e.target.value } : x) })} sx={{ flex: 1 }} />
                  <IconButton size="small" onClick={() => setC({ ...c, parcels: c.parcels.filter((_: any, j: number) => j !== i) })}><Icons.Close fontSize="small" /></IconButton>
                </Box>
              ))}
              <Button size="small" startIcon={<Icons.Add />} sx={STANDARD_TEXT_BUTTON_SX} onClick={() => setC({ ...c, parcels: [...c.parcels, { location: '', chiban: '', land_area: '' }] })}>筆を追加</Button>
            </Box>
          </Box>
        )}
      </Section>

      <Section title="販売の情報（任意）">
        <FormControlLabel control={<Checkbox checked={withSale} onChange={(e) => setWithSale(e.target.checked)} />} label="販売の情報をいま入力する（建築受注・改修・仲介など売上だけの案件はこれだけで登録できる）" />
        {withSale ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mt: 1 }}>
            <Box sx={{ gridColumn: 'span 2' }}>
              <CustomerPicker label="顧客（買主・施主など。複数可。先頭が代表）" value={picker} onChange={(v) => { if (v && !u.customers.some((x: CustomerOption) => x.id === v.id)) setU({ ...u, customers: [...u.customers, v] }); setPicker(null); }} />
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                {u.customers.map((x: CustomerOption, i: number) => <Typography key={x.id} variant="caption" sx={{ px: 1, py: 0.25, bgcolor: 'grey.100', borderRadius: 1 }}>{i === 0 ? '代表 ' : ''}{x.display_name} <span style={{ cursor: 'pointer' }} onClick={() => setU({ ...u, customers: u.customers.filter((y: CustomerOption) => y.id !== x.id) })}>✕</span></Typography>)}
              </Box>
            </Box>
            <TextField select size="small" label="種別" value={u.unit_kind} onChange={(e) => setU({ ...u, unit_kind: e.target.value })}>
              {UNIT_KINDS.map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}
            </TextField>
            <TextField size="small" type="month" label="受注予定年月" value={u.expected_order_ym} onChange={(e) => setU({ ...u, expected_order_ym: e.target.value })} slotProps={{ inputLabel: { shrink: true } }} />
            <TextField size="small" type="date" label="受注確定日" value={u.order_confirmed_date} onChange={(e) => setU({ ...u, order_confirmed_date: e.target.value })} slotProps={{ inputLabel: { shrink: true } }} />
            <AnkenNumberPicker value={u.system_numbers} onChange={(v) => setU({ ...u, system_numbers: v })} />
            <TextField size="small" label="売上（円）" value={u.sales_amount} onChange={(e) => setU({ ...u, sales_amount: e.target.value })} />
            <TextField size="small" label="建築工事費（円）" value={u.construction_cost} onChange={(e) => setU({ ...u, construction_cost: e.target.value })} />
          </Box>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>入力しなくても区画が 1 つ自動で作られます（仕入ありは「販売前」で集計対象外、売上のみは「1.見込み」）。販売を始めたら「販売区画」タブでフェーズを動かしてください。</Typography>
        )}
      </Section>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button onClick={() => navigate('/sfa/projects')} sx={STANDARD_TEXT_BUTTON_SX}>キャンセル</Button>
        <Button onClick={save} startIcon={<Icons.Save />} sx={STANDARD_CTA_BUTTON_SX}>登録する</Button>
      </Box>
    </Box>
  );
}
